import {
    Box, Button, Checkbox, Chip, CircularProgress, InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useEffect, useRef, useState } from 'react';
import CustomAxios from '../../utils/CustomAxios';
import { currentPlanText, customerQuery, outcomeChip, toggleId } from '../../utils/moments';

const PINK = '#ED1E79';
const MUTED = '#6E6478';
const LINE = '#E9E3EE';
const PAGE = 50;
const TONE = {
    ok: { bg: '#E3F4EE', fg: '#0B7A5E' },
    warn: { bg: '#FFF4DC', fg: '#8A5A00' },
    bad: { bg: '#FDECEA', fg: '#B42318' },
};

/**
 * The customers behind the count, searchable by name or mobile.
 *
 * Two modes, set by the Audience step:
 *   filters -- everyone the filters match is ticked; untick to leave someone out.
 *   pick    -- search every customer and tick exactly who gets it.
 */
export function CustomerPicker({ m, set }) {
    const pick = m.audience_mode === 'pick';
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [known, setKnown] = useState({}); // id -> row, so picked chips keep their names
    const seq = useRef(0);

    const filtersKey = JSON.stringify([m.audience_mode, m.inventory_ids, m.segments, m.audience, m.subscription_plan_ids, m.reward_plan_id]);

    const fetchPage = async (offset) => {
        const mine = ++seq.current;
        setLoading(true);
        setError('');
        try {
            const res = await CustomAxios.post('moments/customers/', customerQuery(m, search, offset, PAGE));
            if (mine !== seq.current) return;
            const users = res.data?.users || [];
            setRows((prev) => (offset ? [...prev, ...users] : users));
            setTotal(res.data?.total || 0);
            setKnown((prev) => users.reduce((acc, u) => ({ ...acc, [u.user_id]: u }), prev));
        } catch (e) {
            if (mine === seq.current) setError(e?.response?.data?.error || 'Could not load customers.');
        } finally {
            if (mine === seq.current) setLoading(false);
        }
    };

    useEffect(() => {
        const handle = setTimeout(() => fetchPage(0), 350);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey, search]);

    const ticked = (id) => (pick ? m.user_ids.includes(id) : !m.excluded_user_ids.includes(id));
    const toggle = (id) => {
        if (pick) set({ user_ids: toggleId(m.user_ids, id) });
        else set({ excluded_user_ids: toggleId(m.excluded_user_ids, id) });
    };

    let headline;
    if (pick) headline = `${m.user_ids.length} picked`;
    else if (search.trim()) headline = `${total.toLocaleString('en-IN')} found`;
    else headline = `${Math.max(0, total - m.excluded_user_ids.length).toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')} ticked`;

    return (
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${LINE}` }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={pick ? 'Search any customer by name or mobile' : 'Search these customers by name or mobile'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{headline}</Typography>
                </Stack>
                {!pick && m.excluded_user_ids.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>{m.excluded_user_ids.length} left out</Typography>
                        <Button size="small" onClick={() => set({ excluded_user_ids: [] })}>Tick everyone again</Button>
                    </Stack>
                )}
                {pick && m.user_ids.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75, mt: 1, maxHeight: 96, overflowY: 'auto' }}>
                        {m.user_ids.map((id) => (
                            <Chip key={id} size="small" label={known[id]?.name && known[id].name !== '—' ? known[id].name : (known[id]?.mobileno || `Customer #${id}`)}
                                onDelete={() => toggle(id)} />
                        ))}
                        <Button size="small" onClick={() => set({ user_ids: [] })}>Clear</Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                {rows.map((r) => {
                    const chip = m.reward_plan_id ? outcomeChip(r.plan_outcome) : null;
                    return (
                    <Box key={r.user_id} role="button" onClick={() => toggle(r.user_id)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: .75, borderBottom: `1px solid ${LINE}`, cursor: 'pointer', '&:hover': { bgcolor: '#FCFAFD' } }}>
                        <Checkbox size="small" checked={ticked(r.user_id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(r.user_id)} sx={{ p: .5, '&.Mui-checked': { color: PINK } }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>{r.name}</Typography>
                                <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>{r.mobileno}</Typography>
                            </Stack>
                            <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>
                                {r.hub_id ? r.hub : 'No hub set'} · {r.orders} order{r.orders === 1 ? '' : 's'}{r.last_order_at ? ` · last ${r.last_order_at}` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: r.plan?.active ? '#0B7A5E' : MUTED }} noWrap>
                                {currentPlanText(r.plan)}
                            </Typography>
                        </Box>
                        {chip && (
                            <Chip size="small" label={chip[0]}
                                sx={{ fontSize: 11, fontWeight: 600, bgcolor: TONE[chip[1]].bg, color: TONE[chip[1]].fg, maxWidth: { xs: 120, sm: 'none' } }} />
                        )}
                        {r.segment && <Chip size="small" label={r.segment.replace(' User', '')} sx={{ display: { xs: 'none', sm: 'inline-flex' }, fontSize: 11 }} />}
                        <Tooltip title={r.has_push ? 'Can receive push' : 'No push: app not installed or notifications off. Popup and bell still work.'}>
                            {r.has_push
                                ? <NotificationsActiveOutlinedIcon fontSize="small" sx={{ color: '#0B7A5E' }} />
                                : <NotificationsOffOutlinedIcon fontSize="small" sx={{ color: '#B8AFC0' }} />}
                        </Tooltip>
                    </Box>
                    );
                })}
                {!loading && !rows.length && (
                    <Typography sx={{ p: 3, textAlign: 'center', color: MUTED, fontSize: 14 }}>
                        {error || (search ? 'No customer matches that search.' : 'No customers match these filters.')}
                    </Typography>
                )}
                {loading && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
                {!loading && rows.length < total && (
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button size="small" onClick={() => fetchPage(rows.length)}>Show more ({(total - rows.length).toLocaleString('en-IN')} left)</Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default CustomerPicker;
